/**
 * Корень приложения: загрузка данных, выбор экрана, обработка 401.
 *
 * Поток: вход → `useApp.load()` (bootstrap + срез + черновик) → оболочка с
 * маршрутами. Пока грузится — скелетон §11.1, упало — §11.3 с «Повторить».
 * 401 в любом месте приложения уводит на `/login` с сообщением §12.7, а не
 * на белый экран.
 */
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useApp } from '../store/app-store.js'
import { AppShell } from './AppShell.js'
import { ErrorScreen } from './ErrorScreen.js'
import { LoadingScreen } from './LoadingScreen.js'
import { AppRoutes } from './routes.js'
import { installUnauthorizedHandler } from './session.js'
import { LoginScreen } from '../features/auth/LoginScreen.js'
import { AppBanners } from './AppBanners.js'
import { DraftDock } from '../features/draft/DraftDock.js'

const LOGIN_PATH = '/login'

export function App() {
  const navigate = useNavigate()
  const location = useLocation()

  const status = useApp((s) => s.status)
  const error = useApp((s) => s.error)
  const user = useApp((s) => s.bootstrap?.user)
  const load = useApp((s) => s.load)
  const stopPolling = useApp((s) => s.stopPolling)

  const isLogin = location.pathname === LOGIN_PATH
  const isAuthScreen = isLogin

  /**
   * 401 прилетел асинхронно — запоминаем и уводим на вход в эффекте.
   * Различаем два случая: сессия оборвалась посреди работы (тогда объясняем,
   * что черновик цел) и человек просто открыл адрес, не войдя (тогда молча
   * показываем форму — пугать «сессия закончилась» на первом визите незачем).
   */
  const [expired, setExpired] = useState<null | 'expired' | 'anonymous'>(null)

  useEffect(
    () => installUnauthorizedHandler(() => {
      setExpired(useApp.getState().bootstrap ? 'expired' : 'anonymous')
    }),
    [],
  )

  useEffect(() => {
    if (!expired) return
    const reason = expired
    setExpired(null)
    stopPolling()
    // Сбрасываем только статус: сам черновик на сервере на месте (§12.7).
    useApp.setState({ status: 'idle', error: null, bootstrap: null })
    if (isLogin) return
    navigate(LOGIN_PATH, {
      replace: true,
      state: reason === 'expired'
        ? { reason: 'expired', from: location.pathname + location.search }
        : { from: location.pathname + location.search },
    })
  }, [expired, isLogin, location.pathname, location.search, navigate, stopPolling])

  // Данные тянем только внутри приложения: на экранах входа запрос дал бы 401.
  // Статус читаем из стора, а не из снимка рендера: StrictMode прогоняет эффект
  // дважды, и по снимку bootstrap ушёл бы на сервер два раза.
  useEffect(() => {
    if (isAuthScreen) return
    if (useApp.getState().status === 'idle') void load()
  }, [isAuthScreen, status, load])

  const retry = useCallback(() => { void load() }, [load])

  if (isLogin) return <LoginScreen />

  if (status === 'error') {
    return (
      <AppShell loading={!user}>
        <ErrorScreen message={error} onRetry={retry} />
      </AppShell>
    )
  }

  if (status !== 'ready' || !user) {
    return (
      <AppShell loading>
        <LoadingScreen />
      </AppShell>
    )
  }

  return (
    <AppShell banners={<AppBanners />} dock={<DraftDock />}>
      <AppRoutes />
    </AppShell>
  )
}
